import { supabase, Subcategory, SubcategoryInsert, SubcategoryUpdate } from '../lib/supabase';
import { StorageService } from './storageService';

export class SubcategoryService {
  // Get all subcategories
  static async getSubcategories(): Promise<Subcategory[]> {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .select(`
          *,
          category:categories(name)
        `)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw error;
    }
  }

  // Get subcategories by category ID
  static async getSubcategoriesByCategory(categoryId: string): Promise<Subcategory[]> {
    try {
      console.log('SubcategoryService: Fetching subcategories for category:', categoryId);
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .order('name');

      if (error) {
        console.error('SubcategoryService: Supabase error:', error);
        throw error;
      }
      
      console.log('SubcategoryService: Successfully fetched subcategories:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('SubcategoryService: Error fetching subcategories by category:', error);
      throw error;
    }
  }

  // Get subcategories for product categories only (exclude job subcategories)
  static async getProductSubcategoriesByCategory(categoryId: string): Promise<Subcategory[]> {
    try {
      console.log('SubcategoryService: Fetching product subcategories for category:', categoryId);
      const { data, error } = await supabase
        .from('subcategories')
        .select(`
          *,
          categories!inner(is_job_category)
        `)
        .eq('category_id', categoryId)
        .eq('categories.is_job_category', false)
        .order('name');

      if (error) {
        console.error('SubcategoryService: Supabase error:', error);
        throw error;
      }
      
      console.log('SubcategoryService: Successfully fetched product subcategories:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('SubcategoryService: Error fetching product subcategories by category:', error);
      throw error;
    }
  }

  // Get subcategories for job categories only
  static async getJobSubcategoriesByCategory(categoryId: string): Promise<Subcategory[]> {
    try {
      console.log('SubcategoryService: Fetching job subcategories for category:', categoryId);
      const { data, error } = await supabase
        .from('subcategories')
        .select(`
          *,
          categories!inner(is_job_category)
        `)
        .eq('category_id', categoryId)
        .eq('categories.is_job_category', true)
        .order('name');

      if (error) {
        console.error('SubcategoryService: Supabase error:', error);
        throw error;
      }
      
      console.log('SubcategoryService: Successfully fetched job subcategories:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('SubcategoryService: Error fetching job subcategories by category:', error);
      throw error;
    }
  }

  // Get a single subcategory
  static async getSubcategory(id: string): Promise<Subcategory> {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .select(`
          *,
          category:categories(name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching subcategory:', error);
      throw error;
    }
  }

  // Create a new subcategory
  static async createSubcategory(subcategory: SubcategoryInsert): Promise<Subcategory> {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .insert(subcategory)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating subcategory:', error);
      throw error;
    }
  }

  // Update a subcategory
  static async updateSubcategory(id: string, updates: SubcategoryUpdate): Promise<Subcategory> {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating subcategory:', error);
      throw error;
    }
  }

  // Delete a subcategory
  static async deleteSubcategory(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('subcategories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      throw error;
    }
  }

  // Toggle subcategory active status
  static async toggleSubcategoryStatus(id: string, isActive: boolean): Promise<Subcategory> {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling subcategory status:', error);
      throw error;
    }
  }

  // Create subcategory with image upload
  static async createSubcategoryWithImage(
    subcategoryData: Omit<SubcategoryInsert, 'image_url'>,
    imageFile?: File
  ): Promise<Subcategory> {
    try {
      // Create subcategory first
      const { data: subcategory, error: createError } = await supabase
        .from('subcategories')
        .insert(subcategoryData)
        .select()
        .single();

      if (createError) throw createError;

      // Upload image if provided
      if (imageFile) {
        const imageUrl = await StorageService.uploadSubcategoryImage(subcategory.id, imageFile);
        if (imageUrl) {
          // Update subcategory with image URL
          const { data: updatedSubcategory, error: updateError } = await supabase
            .from('subcategories')
            .update({ image_url: imageUrl })
            .eq('id', subcategory.id)
            .select()
            .single();

          if (updateError) throw updateError;
          return updatedSubcategory;
        }
      }

      return subcategory;
    } catch (error) {
      console.error('Error creating subcategory with image:', error);
      throw error;
    }
  }

  // Update subcategory with image handling
  static async updateSubcategoryWithImage(
    id: string,
    updates: Omit<SubcategoryUpdate, 'image_url'>,
    imageFile?: File,
    imageUrl?: string
  ): Promise<Subcategory> {
    try {
      let finalUpdates: SubcategoryUpdate = { ...updates };

      // Handle image upload/update
      if (imageFile) {
        // Upload new image
        const newImageUrl = await StorageService.uploadSubcategoryImage(id, imageFile);
        if (newImageUrl) {
          finalUpdates.image_url = newImageUrl;
        }
      } else if (imageUrl !== undefined) {
        // Use provided URL (including empty string to remove image)
        finalUpdates.image_url = imageUrl;
      }

      // Update subcategory
      const { data, error } = await supabase
        .from('subcategories')
        .update({
          ...finalUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating subcategory with image:', error);
      throw error;
    }
  }

  // Delete subcategory with image cleanup
  static async deleteSubcategoryWithImage(id: string): Promise<void> {
    try {
      // Get subcategory to check for image
      const { data: subcategory, error: fetchError } = await supabase
        .from('subcategories')
        .select('image_url')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Delete image from storage if it exists
      if (subcategory?.image_url && StorageService.isSubcategoryStorageUrl(subcategory.image_url)) {
        await StorageService.deleteSubcategoryImage(subcategory.image_url);
      }

      // Delete subcategory
      const { error } = await supabase
        .from('subcategories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting subcategory with image:', error);
      throw error;
    }
  }
}
